import React, { useEffect, useState } from 'react';
import { Lock, AlertCircle } from 'lucide-react';

/**
 * Site-wide password gate.
 *
 * SECURITY NOTE: This is a *casual* gate to keep the site out of the public
 * eye. The password is shipped with the client bundle and is therefore
 * inspectable by anyone determined enough. For real authentication, use the
 * existing user/admin/master auth flows.
 */

const SITE_PASSWORD = 'Jordanfirst';
const STORAGE_KEY = 'site_access_granted_v1';

interface SitePasswordGateProps {
  children: React.ReactNode;
}

const SitePasswordGate: React.FC<SitePasswordGateProps> = ({ children }) => {
  const [unlocked, setUnlocked] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'true') {
          setUnlocked(true);
        }
      }
    } catch {
      // localStorage might be unavailable (private mode, SSR, etc.) — gate stays closed.
    } finally {
      setChecked(true);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (password === SITE_PASSWORD) {
      try {
        window.localStorage.setItem(STORAGE_KEY, 'true');
      } catch {
        // Ignore storage errors — user just won't be remembered next visit.
      }
      setUnlocked(true);
    } else {
      setError('Incorrect password. Please try again.');
    }

    setSubmitting(false);
  };

  // Avoid flashing the gate while we read localStorage on initial mount.
  if (!checked) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e3a8a 100%)'
        }}
      />
    );
  }

  if (unlocked) {
    return <>{children}</>;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background:
          'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e3a8a 100%)'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '20px',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
              boxShadow: '0 10px 30px -8px rgba(245, 158, 11, 0.6)'
            }}
          >
            <Lock size={28} color="#fff" />
          </div>
          <h1
            style={{
              color: '#fff',
              fontSize: '24px',
              fontWeight: 700,
              margin: 0,
              marginBottom: '6px'
            }}
          >
            Restricted Access
          </h1>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '14px',
              margin: 0
            }}
          >
            Enter the site password to continue.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#fca5a5',
                background: 'rgba(248, 113, 113, 0.1)',
                border: '1px solid rgba(248, 113, 113, 0.3)',
                borderRadius: '12px',
                padding: '10px 12px',
                marginBottom: '16px',
                fontSize: '13px'
              }}
            >
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError('');
            }}
            placeholder="Site password"
            autoFocus
            autoComplete="current-password"
            aria-label="Site password"
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: '14px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#fff',
              fontSize: '15px',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: '16px'
            }}
          />

          <button
            type="submit"
            disabled={submitting || password.length === 0}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '14px',
              border: 'none',
              background:
                submitting || password.length === 0
                  ? 'rgba(245, 158, 11, 0.4)'
                  : 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
              color: '#fff',
              fontSize: '15px',
              fontWeight: 600,
              cursor:
                submitting || password.length === 0 ? 'not-allowed' : 'pointer',
              boxShadow:
                submitting || password.length === 0
                  ? 'none'
                  : '0 10px 30px -8px rgba(245, 158, 11, 0.6)',
              transition: 'transform 0.15s ease'
            }}
          >
            {submitting ? 'Checking…' : 'Unlock Site'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SitePasswordGate;
